import React, { useState, useMemo } from "react";
import { Search, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { BrandLogo } from "@/components/BrandLogo";
import { EmptyState } from "@/components/EmptyState";
import { format } from "@/lib/brand-tokens";
import { getPaymentItemsByMember, getDealById, getBrandById, DEMO_MEMBER } from "@/lib/mock-data";
import { useStoreVersion } from "@/hooks/useStore";

// MEM-06 — Payment history (member-facing labels, simpler vocabulary)
//
// We translate the admin granular states to user-meaningful labels via
// entity="memberPayment" in <StatusBadge>:
//   Eligible / Scheduled → "Approved for Payment"
//   Sent                 → "Payment Sent"
//   Completed            → "Payment Received"
//   Returned             → "Returned by Bank"
//
// Three tabs collapse the 7-status space into intuitive groups. The stat
// cards on top use the SAME math as the tab buckets so totals always tally.

const TAB_MATCHERS = {
  all:        () => true,
  inProgress: (s) => s === "Eligible" || s === "Scheduled" || s === "Sent",
  completed:  (s) => s === "Completed",
  issues:     (s) => s === "Returned" || s === "Refunded" || s === "Cancelled",
};

const TABS = [
  { id: "all",        label: "All",         emptyText: "No payments yet" },
  { id: "inProgress", label: "In progress", emptyText: "No payments in progress" },
  { id: "completed",  label: "Completed",   emptyText: "No completed payments yet" },
  { id: "issues",     label: "Issues",      emptyText: "No returns or refunds — all clear." },
];

export default function PaymentHistoryPage() {
  useStoreVersion();
  const items = useMemo(() => getPaymentItemsByMember(DEMO_MEMBER.id)
    .sort((a, b) => new Date(b.completedAt || b.sentAt || b.scheduledFor || 0) - new Date(a.completedAt || a.sentAt || a.scheduledFor || 0)),
  []); // eslint-disable-line react-hooks/exhaustive-deps

  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");

  // Bucket sums — used by BOTH the stat cards and the tab counts so the math
  // is single-sourced and always tallies.
  const buckets = useMemo(() => {
    const sums = { all: 0, inProgress: 0, completed: 0, issues: 0 };
    const counts = { all: 0, inProgress: 0, completed: 0, issues: 0 };
    items.forEach((p) => {
      sums.all += p.amount || 0;
      counts.all += 1;
      ["inProgress", "completed", "issues"].forEach((b) => {
        if (TAB_MATCHERS[b](p.status)) {
          sums[b] += p.amount || 0;
          counts[b] += 1;
        }
      });
    });
    return { sums, counts };
  }, [items]);

  const filtered = useMemo(() => items.filter((p) => {
    if (!TAB_MATCHERS[tab](p.status)) return false;
    if (q) {
      const deal = getDealById(p.dealId);
      const brand = getBrandById(p.brandId || deal?.brandId);
      const hay = `${brand?.name || ""} ${deal?.name || ""} ${p.refNumber || ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [items, tab, q]);

  const filteredTotal = filtered.reduce((a, x) => a + (x.amount || 0), 0);
  const issuesCount = buckets.counts.issues;

  return (
    <div data-testid="payment-history-page" className="space-y-6 pb-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Earnings</div>
          <h1 className="font-brand text-display text-ink leading-tight mt-1">Payment history</h1>
          <p className="text-body-sm text-ink-muted mt-1">{items.length} payments to bank account ending {DEMO_MEMBER.accountLast4}</p>
        </div>
        <Button variant="outline" data-testid="member-export-csv-btn">
          <Download className="size-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Stat cards — math is single-sourced from `buckets` so subtotals
          always equal the corresponding tab's total. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          testid="stat-completed"
          label="Lifetime received"
          value={format.currency(buckets.sums.completed)}
          sub={`${buckets.counts.completed} payment${buckets.counts.completed === 1 ? "" : "s"} received`}
          tone="brand"
        />
        <StatCard
          testid="stat-in-progress"
          label="In progress"
          value={format.currency(buckets.sums.inProgress)}
          sub={`${buckets.counts.inProgress} approved & en route`}
        />
        <StatCard
          testid="stat-issues"
          label={issuesCount === 0 ? "Returned" : "Needs attention"}
          value={issuesCount}
          sub={issuesCount === 0 ? "All payments delivered" : "Returned, refunded or cancelled"}
          tone={issuesCount > 0 ? "warn" : "default"}
        />
      </div>

      {/* Tabbed table */}
      <div className="bg-surface border border-line rounded-xl">
        <div className="px-2 pt-2 border-b border-line flex flex-wrap items-center justify-between gap-2">
          <nav role="tablist" className="flex items-center gap-1" data-testid="payment-tabs">
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active}
                  data-testid={`payment-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`relative px-3.5 py-2.5 text-body-sm font-medium transition-colors ${
                    active ? "text-ink" : "text-ink-muted hover:text-ink"
                  }`}
                >
                  {t.label}
                  <span className={`ml-2 inline-flex items-center justify-center px-1.5 min-w-[20px] h-5 rounded-full text-[11px] tabular-nums ${
                    active ? "bg-brand-700 text-white" : "bg-surface-alt text-ink-muted"
                  }`}>{buckets.counts[t.id]}</span>
                  {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-brand-700 rounded-t" />}
                </button>
              );
            })}
          </nav>
          <div className="relative max-w-xs w-full sm:w-72 px-2 pb-2 sm:pb-0">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-ink-hint" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by brand, deal, ref…" className="pl-9 h-9" data-testid="payment-search" />
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState title={TABS.find(t => t.id === tab).emptyText} description="Once cards in a reservation are approved, payments appear here on the deal's payment terms." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-body-sm">
              <thead className="bg-surface-alt">
                <tr className="text-left">
                  <th className="px-6 py-3 text-eyebrow uppercase text-ink-hint">Date</th>
                  <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Deal · Brand</th>
                  <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Bank</th>
                  <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Amount</th>
                  <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Status</th>
                  <th className="px-6 py-3 text-eyebrow uppercase text-ink-hint">Reference</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/60">
                {filtered.map((p) => {
                  const deal  = getDealById(p.dealId);
                  const brand = getBrandById(p.brandId || deal?.brandId);
                  const dateStr = p.completedAt || p.sentAt || p.scheduledFor;
                  return (
                    <tr key={p.id} className="hover:bg-surface-alt/40" data-testid={`payment-row-${p.id}`}>
                      <td className="px-6 py-3.5 text-ink-muted text-caption tabular-nums whitespace-nowrap">
                        {dateStr ? <>
                          <div className="text-ink">{format.date(dateStr)}</div>
                          <div className="text-ink-subtle text-[11px]">{format.relative(dateStr)}</div>
                        </> : <span className="text-ink-hint">—</span>}
                      </td>
                      <td className="px-3 py-3.5">
                        <div className="flex items-center gap-2.5">
                          {brand && <BrandLogo brand={brand} size="xs" />}
                          <div className="min-w-0">
                            <div className="text-ink font-medium truncate max-w-[240px]">{deal?.name || "—"}</div>
                            <div className="text-caption text-ink-subtle truncate max-w-[240px]">{brand?.name || "—"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3.5 font-mono text-code text-ink-muted">{format.bankLast4(p.bankAccountLast4 || p.bankLast4)}</td>
                      <td className="px-3 py-3.5 font-semibold text-ink tabular-nums text-right">{format.currency(p.amount)}</td>
                      <td className="px-3 py-3.5">
                        <StatusBadge entity="memberPayment" status={p.status} dot />
                        {p.returnReason && (
                          <div className="mt-1 text-caption text-red-600">{p.returnReason}</div>
                        )}
                      </td>
                      <td className="px-6 py-3.5 font-mono text-code text-ink-subtle">{p.refNumber || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-surface-alt border-t border-line">
                <tr>
                  <td colSpan={3} className="px-6 py-3 text-eyebrow uppercase text-ink-hint">
                    {tab === "all" ? `Total · ${filtered.length} payment${filtered.length === 1 ? "" : "s"}` : `${TABS.find(t => t.id === tab).label} subtotal · ${filtered.length}`}
                  </td>
                  <td className="px-3 py-3 text-right font-bold text-ink tabular-nums" data-testid="payment-history-total">{format.currency(filteredTotal)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, tone = "default", testid }) {
  const valueCls = tone === "brand" ? "text-brand-700"
    : tone === "warn" ? "text-orange-600"
    : "text-ink";
  return (
    <div data-testid={testid} className="bg-surface border border-line rounded-xl p-6">
      <div className="text-caption text-ink-subtle">{label}</div>
      <div className={`mt-2 font-brand text-[34px] font-bold leading-none tabular-nums ${valueCls}`}>{value}</div>
      <div className="mt-2 text-caption text-ink-subtle">{sub}</div>
    </div>
  );
}
